/**
 * The full role union as the API defines it today (`@mlm/shared`
 * `PayoutMethodField['role']`), INCLUDING the contact-autofill roles. Declared
 * structurally here because the member SPA's own `PayoutMethodField` mirror in
 * `lib/api.ts` still carries the pre-autofill union — `{ role?: string }`
 * accepts both widths, so callers can pass a field from either type.
 */
type CashoutLockableField = { role?: string };

/**
 * Decide whether a cash-out account field renders LOCKED — read-only, showing a
 * value taken from the member's own profile — and if so with which value and
 * which caption under the input.
 *
 * Non-null for exactly three cases:
 *  - the name lock (`lock_cashout_name_to_owner`) on an `account_holder` field
 *    with a non-empty profile full name;
 *  - locked contact autofill (`cashout_contact_autofill === 'locked'`) on an
 *    `email` field with a non-empty profile email;
 *  - locked contact autofill on a `contact_number` field with a non-empty
 *    profile phone.
 *
 * Everything else — including `prefill` mode, which seeds the same values but
 * keeps them editable — returns null (normal editable field).
 *
 * Empty-profile fallback: a locked-mode field whose profile value is EMPTY
 * (`users.phone` is nullable) is deliberately NOT locked. The server only
 * overwrites non-empty profile values, so freezing an empty input here would
 * brick the member on a required field they can never fill. It stays editable
 * and required-enforced instead.
 */
export function resolveCashoutFieldLock(
  field: CashoutLockableField,
  ctx: {
    lockName: boolean;
    contactMode: 'off' | 'prefill' | 'locked';
    ownerFullName: string;
    ownerEmail: string;
    ownerPhone: string;
  },
): { value: string; caption: string } | null {
  if (ctx.lockName && field.role === 'account_holder' && ctx.ownerFullName !== '') {
    return { value: ctx.ownerFullName, caption: 'Locked to your account name.' };
  }
  if (ctx.contactMode === 'locked') {
    if (field.role === 'email' && ctx.ownerEmail !== '') {
      return { value: ctx.ownerEmail, caption: 'Locked to your account email.' };
    }
    if (field.role === 'contact_number' && ctx.ownerPhone !== '') {
      return { value: ctx.ownerPhone, caption: 'Locked to your account contact number.' };
    }
  }
  return null;
}
